import React from "react";
import { useEffect, useState } from 'react';

/**
 * PROFESSIONAL LOADING SPINNER USAGE GUIDE
 * ==========================================
 *
 * This component demonstrates best practices for loading indicators in React applications.
 * The loading system uses a combination of immediate button feedback and proper state management.
 *
 * LOADING PATTERNS IMPLEMENTED:
 * =============================
 *
 * 1. IMMEDIATE BUTTON FEEDBACK
 *    - Button shows spinner immediately on click
 *    - Prevents double-clicks with disabled state
 *    - No delay - instant visual feedback
 *
 * 2. REACT STATE BATCHING SOLUTION
 *    - setTimeout wrapper allows React to process state updates
 *    - Prevents UI blocking during async operations
 *    - Ensures smooth user experience
 *
 * 3. CLEANUP AND ERROR HANDLING
 *    - Automatic state cleanup after operations
 *    - Comprehensive error handling with user feedback
 *    - Consistent timing for success/error messages
 *
 * USAGE PATTERNS FOR FUTURE COMPONENTS:
 * ====================================
 *
 * Pattern 1: Button-Level Loading (Most Common)
 * ```typescript
 * const [isSubmitting, setIsSubmitting] = useState(false);
 *
 * <Button
 *   loading={isSubmitting}        // ← Immediate spinner
 *   disabled={isSubmitting}       // ← Prevents double-clicks
 *   onClick={async () => {
 *     setIsSubmitting(true);      // ← Start immediately
 *     try {
 *       await apiCall();
 *       showSuccessMessage();
 *     } catch (error) {
 *       showErrorMessage();
 *     } finally {
 *       setIsSubmitting(false);   // ← Always cleanup
 *     }
 *   }}
 * />
 * ```
 *
 * Pattern 2: Page-Level Loading (For Initial Data)
 * ```typescript
 * const [pageLoading, setPageLoading] = useState(true);
 *
 * useEffect(() => {
 *   loadData().finally(() => setPageLoading(false));
 * }, []);
 *
 * if (pageLoading) {
 *   return <LoadingSpinner />; // Full page overlay
 * }
 * ```
 *
 * Pattern 3: Overlay Loading (For Long Operations > 3s)
 * ```typescript
 * const [showOverlay, setShowOverlay] = useState(false);
 *
 * {showOverlay && (
 *   <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
 *     <div className="bg-white p-8 rounded-lg shadow-xl">
 *       <Spinner />
 *       <p>Processing... This may take a moment</p>
 *     </div>
 *   </div>
 * )}
 * ```
 *
 * Pattern 4: Toast Integration (For Operation Feedback)
 * ```typescript
 * // Single summary toast instead of multiple
 * const showResultToast = (successCount: number, errorCount: number) => {
 *   if (successCount > 0 && errorCount === 0) {
 *     // All successful
 *     toast.success(`✅ All ${successCount} operations completed successfully`);
 *   } else if (successCount > 0 && errorCount > 0) {
 *     // Partial success
 *     toast.warning(`⚠️ ${successCount} succeeded, ${errorCount} failed`);
 *   } else {
 *     // All failed
 *     toast.error(`❌ All ${errorCount} operations failed`);
 *   }
 * };
 * ```
 *
 * TIMING GUIDELINES:
 * ==================
 * - Success messages: 2000ms (2 seconds)
 * - Error messages: 4000ms (4 seconds - give users more time to read)
 * - Loading overlays: Remove immediately when operation completes
 * - Button spinners: Keep until operation completes
 *
 * BEST PRACTICES:
 * ===============
 * 1. Always show immediate feedback (don't wait for API calls)
 * 2. Use setTimeout for React state batching when needed
 * 3. Provide clear progress messages
 * 4. Handle errors gracefully with user-friendly messages
 * 5. Clean up state properly to prevent memory leaks
 * 6. Consider accessibility (screen readers, keyboard navigation)
 * 7. Test loading states in different scenarios (success, error, slow network)
 *
 * This implementation provides excellent UX with minimal performance impact.
 */
import { useNavigate } from "react-router-dom";
import { UniqueUser } from "../../types";
import eventBus from "../../eventBus";
import { useMemo } from 'react';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';

import {
  addLicensesToUsers,
  removeLicensesFromUsers,
  getLicenseCountsForUsers,
  getAllTools,
  getLicServers,
  getInfraServers,
  fetchLicensesForUserId,
  fetchAllUsers,
  updateUniqueUsers,
  generateNfsHome,
  patchUniqueUserProgram,
} from '../../services/api';
import { useNewGui } from '../../hooks/useNewGui';
import { useAuthGuard } from '../../hooks/useAuthGuard';
import { useRolePreview } from '../../contexts/RolePreviewContext';
import { useSystemSettings } from '../../hooks/useSystemSettings';
import AttachExistingUsersModal from '../workspace/AttachExistingUsersModal';
import ClassifyProgramsReviewModal from './ClassifyProgramsReviewModal';


interface ColumnConfig {
    key: keyof UniqueUser;
    label: string;
    visible: boolean;
    sortable?: boolean;
}

interface UniqueUserTableProps {
    uniqueUsers: UniqueUser[];
    onUsersUpdated?: () => void | Promise<void>;
  }

  const isOrphanResidual = (value: UniqueUser['membershipResidual']): boolean =>
    value === true || value === 'true' || value === 'orphan';

  const formatMembershipResidual = (value: UniqueUser['membershipResidual']): string => {
    if (value === null || value === undefined || value === '') return '—';
    if (isOrphanResidual(value)) return 'Orphan';
    if (value === false || value === 'false') return 'No';
    return String(value);
  };

  const UniqueUserTable :React.FC<UniqueUserTableProps> = ({ uniqueUsers, onUsersUpdated }) => {
    const navigate = useNavigate();
    const useNew = useNewGui();
    const { user } = useAuthGuard();
    const { effectiveRole } = useRolePreview();
    const role = effectiveRole || user?.role;
    const canManagePrograms = role === 'itadmin' || role === 'superadmin';
    const { settings } = useSystemSettings();
    const workspaceEnabled = settings.WORKSPACE_CONTAINER_ENABLED === 'true';
    console.log('UniqueUserTable Props:', uniqueUsers);

    const [usersWithLicenseCounts, setUsersWithLicenseCounts] = useState<(UniqueUser & { licenseCount: number })[]>([]);
    const [selectedRows, setSelectedRows] = useState<UniqueUser[]>([]);
    // IC-042: bulk "Generate NFS Home" from this table's existing multi-select
    const [bulkNfsHomeRunning, setBulkNfsHomeRunning] = useState(false);
    const [bulkNfsHomeResults, setBulkNfsHomeResults] = useState<
      Array<{ userId: string; outcome: 'already-ready' | 'success' | 'failed'; detail: string }>
    >([]);
    const [bulkNfsHomeResultsVisible, setBulkNfsHomeResultsVisible] = useState(false);
    const [attachModalOpen, setAttachModalOpen] = useState(false);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    // const [selectedTools, setSelectedTools] = useState<ToolEntity[]>([]);
    const [selectedTools, setSelectedTools] = useState<string[]>([]);
    const [numLics, setNumLics] = useState<number>(1);
    const [licenseCounts, setLicenseCounts] = useState<{ [userId: string]: number }>({});
    const [tools, setTools] = useState<any[]>([]);
    const [toolsLoading, setToolsLoading] = useState<boolean>(true);
    const [selectedServer, setSelectedServer] = useState<string | null>(null);
    const [selectedLicServer, setSelectedLicServer] = useState<string | null>(null);
    // Modal state management
    const [addLicenseModalVisible, setAddLicenseModalVisible] = useState(false);
    const [updateLicenseModalVisible, setUpdateLicenseModalVisible] = useState(false);
    const [licenseEndDate, setLicenseEndDate] = useState<Date | null>(null);
    const [licenseDuration, setLicenseDuration] = useState<number>(90); // Default 90 days
    const [currentLicenses, setCurrentLicenses] = useState<any[]>([]);
    const [selectedViewTools, setSelectedViewTools] = useState<string[]>([]);
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
    const [endDateFilter, setEndDateFilter] = useState<Date | null>(null);
    const [endDateOperator, setEndDateOperator] = useState<'gte' | 'lte'>('gte');

    // License addition loading state
    const [addLicenseLoading, setAddLicenseLoading] = useState(false);
    const [addLicenseMessage, setAddLicenseMessage] = useState('');

    // Helper function for consistent result display timing
    const showResultAndHide = (message: string, isSuccess: boolean) => {
      setAddLicenseMessage(message);
      console.log('Add License Result:', message);

      const duration = isSuccess ? 2000 : 4000; // Success: 2s, Error: 4s

      setTimeout(() => {
        setAddLicenseLoading(false);
        setAddLicenseMessage('');
      }, duration);
    };

    // Column configuration state
    const [columnConfigOpen, setColumnConfigOpen] = useState(false);

    // Top bar filter states
    const [globalFilter, setGlobalFilter] = useState('');
    const [licenseFilter, setLicenseFilter] = useState<number | string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string | null>(null);
    const [programCodeFilter, setProgramCodeFilter] = useState<string | null>(null);
    const [programKindFilter, setProgramKindFilter] = useState<string | null>(null);
    const [membershipResidualFilter, setMembershipResidualFilter] = useState<string | null>(null);

    // Program classification / override
    const [classifyReviewOpen, setClassifyReviewOpen] = useState(false);
    const [classifyIntent, setClassifyIntent] = useState<'previewOnly' | 'applyFlow'>('previewOnly');
    const [attachFromClassifyUsers, setAttachFromClassifyUsers] = useState<
      { id: number | string; userId: string; email?: string | null; programCode?: string | null }[] | null
    >(null);
    const [programEditModalVisible, setProgramEditModalVisible] = useState(false);
    const [programEditUser, setProgramEditUser] = useState<UniqueUser | null>(null);
    const [programEditSaving, setProgramEditSaving] = useState(false);
    const [editProgramCode, setEditProgramCode] = useState('');
    const [editProgramKind, setEditProgramKind] = useState('');
    const [editProgramInstanceId, setEditProgramInstanceId] = useState('');
    const [editMembershipResidual, setEditMembershipResidual] = useState<string | null>(null);

    // Update users functionality
    const [updatingUsers, setUpdatingUsers] = useState(false);

    const refreshUserData = async () => {
        const [data, counts] = await Promise.all([
            fetchAllUsers(),
            getLicenseCountsForUsers(),
        ]);
        setLicenseCounts(counts);
        setUsersWithLicenseCounts(data.map((u: UniqueUser) => ({
            ...u,
            licenseCount: counts[u.userId] || 0,
        })));
        await onUsersUpdated?.();
    };

    const handleUpdateUsers = async () => {
        setUpdatingUsers(true);
        try {
            await updateUniqueUsers();
            console.log('Unique users update completed successfully');
            await refreshUserData();

            eventBus.emit('show-toast', {
                summary: '✅ Users Updated',
                message: 'Unique users information has been refreshed successfully',
                severity: 'success'
            });

        } catch (error) {
            console.error('Error updating unique users:', error);
            eventBus.emit('show-toast', {
                summary: '❌ Update Failed',
                message: 'Failed to update unique users information',
                severity: 'error'
            });
        } finally {
            setUpdatingUsers(false);
        }
    };

    const openClassifyReview = (intent: 'previewOnly' | 'applyFlow') => {
        setClassifyIntent(intent);
        setClassifyReviewOpen(true);
    };

    const openProgramEditModal = (row: UniqueUser) => {
        setProgramEditUser(row);
        setEditProgramCode(row.programCode || '');
        setEditProgramKind(row.programKind || '');
        setEditProgramInstanceId(row.programInstanceId || '');
        setEditMembershipResidual(
            isOrphanResidual(row.membershipResidual) ? 'orphan'
                : row.membershipResidual === false || row.membershipResidual === 'false' ? 'none'
                    : row.membershipResidual != null && row.membershipResidual !== ''
                        ? String(row.membershipResidual)
                        : null,
        );
        setProgramEditModalVisible(true);
    };

    const handleSaveProgramOverride = async () => {
        if (!programEditUser) return;
        if (!editProgramCode.trim()) {
            eventBus.emit('show-toast', {
                summary: 'Validation Error',
                message: 'Program code is required',
                severity: 'error',
            });
            return;
        }

        setProgramEditSaving(true);
        try {
            const membershipResidual = editMembershipResidual === 'orphan'
                ? true
                : editMembershipResidual === 'none'
                    ? false
                    : editMembershipResidual || undefined;

            await patchUniqueUserProgram(programEditUser.id, {
                programCode: editProgramCode.trim(),
                ...(editProgramKind.trim() && { programKind: editProgramKind.trim() }),
                ...(editProgramInstanceId.trim() && { programInstanceId: editProgramInstanceId.trim() }),
                ...(membershipResidual !== undefined && { membershipResidual }),
            });

            eventBus.emit('show-toast', {
                summary: 'Program updated',
                message: `Program override saved for ${programEditUser.email}`,
                severity: 'success',
            });

            setProgramEditModalVisible(false);
            setProgramEditUser(null);
            await refreshUserData();
        } catch (error) {
            console.error('Program override failed:', error);
            eventBus.emit('show-toast', {
                summary: 'Update failed',
                message: (error as Error)?.message || 'Failed to save program override',
                severity: 'error',
            });
        } finally {
            setProgramEditSaving(false);
        }
    };

    // Filtered data based on top bar filters
    const filteredUsers = useMemo(() => {
        return usersWithLicenseCounts.filter(user => {
            // Global search filter - search across multiple fields
            const globalMatch = globalFilter === '' ||
                user.userId.toLowerCase().includes(globalFilter.toLowerCase()) ||
                (user.userIds && user.userIds.some(id => id.toLowerCase().includes(globalFilter.toLowerCase()))) ||
                user.email.toLowerCase().includes(globalFilter.toLowerCase()) ||
                (user.fullName && user.fullName.toLowerCase().includes(globalFilter.toLowerCase())) ||
                (user.mobile && user.mobile.includes(globalFilter)) ||
                (user.servers && user.servers.toLowerCase().includes(globalFilter.toLowerCase())) ||
                (user.programCode && user.programCode.toLowerCase().includes(globalFilter.toLowerCase())) ||
                (user.programKind && user.programKind.toLowerCase().includes(globalFilter.toLowerCase()));

            // License count filter
            const licenseMatch = !licenseFilter ||
                (licenseFilter === 0 && user.licenseCount === 0) ||
                (licenseFilter === '1-5' && user.licenseCount >= 1 && user.licenseCount <= 5) ||
                (licenseFilter === '6+' && user.licenseCount >= 6);

            // Status filter based on numActiveAccounts
            const statusMatch = !statusFilter ||
                (statusFilter === 'active' && (user.numActiveAccounts || 0) > 0) ||
                (statusFilter === 'inactive' && (user.numActiveAccounts || 0) === 0);

            const programCodeMatch = !programCodeFilter ||
                (programCodeFilter === '__unclassified__'
                    ? !user.programCode
                    : user.programCode === programCodeFilter);

            const programKindMatch = !programKindFilter ||
                (programKindFilter === '__unset__'
                    ? !user.programKind
                    : user.programKind === programKindFilter);

            const membershipResidualMatch = !membershipResidualFilter ||
                (membershipResidualFilter === 'orphan' && isOrphanResidual(user.membershipResidual)) ||
                (membershipResidualFilter === 'none' && !isOrphanResidual(user.membershipResidual) &&
                    (user.membershipResidual === false || user.membershipResidual === 'false' ||
                     user.membershipResidual == null || user.membershipResidual === ''));

            return globalMatch && licenseMatch && statusMatch &&
                programCodeMatch && programKindMatch && membershipResidualMatch;
        });
    }, [usersWithLicenseCounts, globalFilter, licenseFilter, statusFilter,
        programCodeFilter, programKindFilter, membershipResidualFilter]);

    const programFilterOptions = useMemo(() => {
        const codes = new Set<string>();
        const kinds = new Set<string>();
        for (const user of usersWithLicenseCounts) {
            if (user.programCode) codes.add(user.programCode);
            if (user.programKind) kinds.add(user.programKind);
        }
        return {
            programCodes: [
                { label: 'All', value: null },
                { label: 'Unclassified', value: '__unclassified__' },
                ...Array.from(codes).sort().map((code) => ({ label: code, value: code })),
            ],
            programKinds: [
                { label: 'All', value: null },
                { label: 'Unset', value: '__unset__' },
                ...Array.from(kinds).sort().map((kind) => ({ label: kind, value: kind })),
            ],
            membershipResiduals: [
                { label: 'All', value: null },
                { label: 'Orphan', value: 'orphan' },
                { label: 'None', value: 'none' },
            ],
        };
    }, [usersWithLicenseCounts]);

    const programSummary = useMemo(() => {
        const counts: Record<string, number> = {};
        let orphanCount = 0;
        for (const user of filteredUsers) {
            const code = user.programCode?.trim() || '(unclassified)';
            counts[code] = (counts[code] || 0) + 1;
            if (isOrphanResidual(user.membershipResidual)) {
                orphanCount += 1;
            }
        }
        return { counts, orphanCount };
    }, [filteredUsers]);

    const clearTopBarFilters = () => {
        setGlobalFilter('');
        setLicenseFilter(null);
        setStatusFilter(null);
        setProgramCodeFilter(null);
        setProgramKindFilter(null);
        setMembershipResidualFilter(null);
    };

    // Function that returns default UniqueUserTable column configuration
    const getDefaultUniqueUserColumns = (): ColumnConfig[] => [
        { key: 'sid', label: 'SID', visible: true, sortable: true },
        { key: 'userId', label: 'User ID', visible: true, sortable: true },
        { key: 'email', label: 'Email', visible: true, sortable: true },
        { key: 'fullName', label: 'Full Name', visible: false, sortable: true },
        { key: 'mobile', label: 'Mobile', visible: false, sortable: true },
        { key: 'servers', label: 'Servers', visible: true, sortable: true },
        { key: 'numActiveAccounts', label: '# Active Accts', visible: true, sortable: true },
        { key: 'programCode', label: 'Program', visible: true, sortable: true },
        { key: 'programKind', label: 'Kind', visible: false, sortable: true },
        { key: 'programInstanceId', label: 'Instance', visible: true, sortable: true },
        { key: 'primaryGroupInstance', label: 'Group Instance (Primary)', visible: true, sortable: true },
        { key: 'membershipResidual', label: 'Residual', visible: false, sortable: true },
        { key: 'createdAt', label: 'Created At', visible: false, sortable: true },
    ];

    const [columns, setColumns] = useState<ColumnConfig[]>(getDefaultUniqueUserColumns());

    console.log('Selected Rows:', selectedRows);
    console.log('Selected Tools:', selectedTools);
    console.log('Num Licenses:', numLics);




    // Load column preferences from localStorage - SMART LOADING
    useEffect(() => {
        const savedConfig = localStorage.getItem('uniqueUserTableColumnConfig');
        if (savedConfig) {
            try {
                const parsedConfig = JSON.parse(savedConfig);
                const defaultColumns = getDefaultUniqueUserColumns();

                // Prefer defaults as source of truth so newly added columns appear
                // even when localStorage has an older key set.
                setColumns(
                    defaultColumns.map((col) => ({
                        ...col,
                        visible: parsedConfig[col.key] !== undefined
                            ? parsedConfig[col.key]
                            : col.visible,
                    }))
                );
            } catch (error) {
                console.warn('Failed to load column config, using defaults:', error);
                setColumns(getDefaultUniqueUserColumns());
            }
        } else {
            // No saved config, use smart defaults
            setColumns(getDefaultUniqueUserColumns());
        }
    }, []);

    // Save column preferences to localStorage
    const saveColumnConfig = (newColumns: ColumnConfig[]) => {
        const config = newColumns.reduce((acc, col) => {
            acc[col.key] = col.visible;
            return acc;
        }, {} as Record<string, boolean>);
        localStorage.setItem('uniqueUserTableColumnConfig', JSON.stringify(config));
    };

    // Handle column visibility toggle
    const handleColumnToggle = (key: keyof UniqueUser) => {
        setColumns(prevColumns => {
            const newColumns = prevColumns.map(col =>
                col.key === key ? { ...col, visible: !col.visible } : col
            );
            saveColumnConfig(newColumns);
            return newColumns;
        });
    };

    // Reset columns to default visibility
    const resetColumnsToDefault = () => {
        const defaultColumns = getDefaultUniqueUserColumns();
        setColumns(defaultColumns);
        saveColumnConfig(defaultColumns);
    };

    useEffect(() => {
        const loadLicenseCounts = async () => {
            try {
                console.log("Fetching license counts for all users");
                const counts = await getLicenseCountsForUsers();
                console.log("License counts:", counts);
                setLicenseCounts(counts);

                // Create users with license counts for DataTable
                const usersWithCounts = uniqueUsers.map(user => ({
                    ...user,
                    licenseCount: counts[user.userId] || 0
                }));
                setUsersWithLicenseCounts(usersWithCounts);
            } catch (error) {
                console.error("Error fetching license counts:", error);
            }
        };

        if (uniqueUsers.length > 0) {
            loadLicenseCounts();
        }
    }, [uniqueUsers]);

    useEffect(() => {
        const loadTools = async () => {
            try {
                console.log("Fetching available tools for UniqueUserTable");
                setToolsLoading(true);
                const toolsData = await getAllTools();
                console.log("Tools Data for UniqueUserTable:", toolsData);
                if (toolsData) {
                    // Transform tools data to MultiSelect format
                    const toolOptions = toolsData.map((tool: any) => ({
                        label: tool.name,
                        value: tool.name
                    }));
                    setTools(toolOptions);
                }
            } catch (error) {
                console.error("Error fetching tools for UniqueUserTable:", error);
                eventBus.emit('show-toast', {
                    summary: 'Error loading tools',
                    message: 'Failed to load available tools. Please refresh the page.',
                    severity: 'error',
                });
            } finally {
                setToolsLoading(false);
            }
        };

        loadTools();
    }, []);

    // Load server options for license addition
    const [servers, setServers] = useState<any[]>([]);
    const [licenseServers, setLicenseServers] = useState<any[]>([]);
    const [serversLoading, setServersLoading] = useState<boolean>(true);

    // Load current licenses for selected users with batch processing and smart filtering
    const loadCurrentLicenses = async () => {
        try {
            console.log("Loading current licenses for selected users");

            // Smart filtering: Only load licenses for users who actually have licenses
            const usersWithLicenses = selectedRows.filter((user: UniqueUser) =>
                licenseCounts[user.userId] && licenseCounts[user.userId] > 0
            );

            // If no users have licenses, show empty modal with message
            if (usersWithLicenses.length === 0) {
                setCurrentLicenses([]);
                setUpdateLicenseModalVisible(true);
                return;
            }

            const totalUsers = usersWithLicenses.length;
            const BATCH_SIZE = 5; // Process 5 users at a time to prevent overload

            // Show loading indicator for large selections (removed toast, using only UI feedback)
            // The button already shows loading state

            const batches: UniqueUser[][] = [];
            for (let i = 0; i < usersWithLicenses.length; i += BATCH_SIZE) {
                batches.push(usersWithLicenses.slice(i, i + BATCH_SIZE));
            }

            let allLicenses: any[] = [];
            let processedUsers = 0;

            for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
                const batch = batches[batchIndex];
                try {
                    const batchLicenses = await Promise.all(
                        batch.map((user: UniqueUser) => fetchLicensesForUserId(user.userId))
                    );
                    const flatBatchLicenses = batchLicenses.flat().filter((license: any) =>
                        license !== null && license !== undefined
                    );
                    allLicenses = allLicenses.concat(flatBatchLicenses);
                    processedUsers += batch.length;

                    // Progress update removed - using single summary toast instead
                } catch (batchError) {
                    console.error(`Error loading batch ${batchIndex + 1}:`, batchError);
                    // Continue with other batches even if one fails
                    // Removed individual error toasts - using single summary approach
                }
            }

            console.log("Current licenses loaded:", allLicenses);
            setCurrentLicenses(allLicenses);

            // Success message for large selections
            if (totalUsers > 10) {
                eventBus.emit('show-toast', {
                    summary: 'Licenses Loaded',
                    message: `Successfully loaded ${allLicenses.length} licenses for ${processedUsers} users.`,
                    severity: 'success'
                });
            }

        } catch (error) {
            console.error("Error loading current licenses:", error);
            eventBus.emit('show-toast', {
                summary: 'Error loading licenses',
                message: 'Failed to load current license information. Please try selecting fewer users or try again.',
                severity: 'error',
            });
        }
    };

    // IC-042: bulk "Generate NFS Home" for the currently selected rows. Sequential with a
    // small delay between calls (not parallel) to avoid bursting FreeIPA with concurrent
    // user_add requests — same pacing rationale as IC-038's notification-send spacing.
    const BULK_NFS_HOME_DELAY_MS = 400;
    const handleBulkGenerateNfsHome = async () => {
        if (selectedRows.length === 0) return;
        setBulkNfsHomeRunning(true);
        setBulkNfsHomeResults([]);
        const results: Array<{ userId: string; outcome: 'already-ready' | 'success' | 'failed'; detail: string }> = [];

        for (const user of selectedRows) {
            if (user.homeReady) {
                results.push({ userId: user.userId, outcome: 'already-ready', detail: user.homePath || '' });
                continue;
            }
            try {
                const result = await generateNfsHome(user.userId);
                const detail = !result.ipaSimulated && !result.homeSimulated
                    ? `NFS home ready: ${result.homePath}`
                    : !result.ipaSimulated
                        ? `IPA user confirmed. NFS home creation still simulated (${result.homePath})`
                        : `Simulated — not configured yet (${result.homePath})`;
                results.push({ userId: user.userId, outcome: 'success', detail });
            } catch (error: any) {
                results.push({
                    userId: user.userId,
                    outcome: 'failed',
                    detail: error?.response?.data?.message || 'Failed to generate NFS home',
                });
            }
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => setTimeout(resolve, BULK_NFS_HOME_DELAY_MS));
        }

        setBulkNfsHomeResults(results);
        setBulkNfsHomeRunning(false);
        setBulkNfsHomeResultsVisible(true);
    };

    // Clear all view filters
    const clearAllViewFilters = () => {
        setSelectedViewTools([]);
        setSelectedStatuses([]);
        setEndDateFilter(null);
        setEndDateOperator('gte');
    };

    // Check if any view filters are active
    const hasActiveViewFilters = selectedViewTools.length > 0 ||
                                selectedStatuses.length > 0 ||
                                endDateFilter !== null;

    // Handle adding licenses with full parameters
    const handleAddLicenses = async () => {
        // Validate required fields
        if (selectedTools.length === 0) {
            eventBus.emit('show-toast', {
                summary: 'Validation Error',
                message: 'Please select at least one tool',
                severity: 'error'
            });
            return;
        }

        if (!numLics || numLics < 1) {
            eventBus.emit('show-toast', {
                summary: 'Validation Error',
                message: 'Please specify a valid number of licenses',
                severity: 'error'
            });
            return;
        }

        // ✅ Show loading indicator immediately (urgent update)
        setAddLicenseLoading(true);
        setAddLicenseMessage(`Adding ${selectedTools.length} license${selectedTools.length > 1 ? 's' : ''} for ${selectedRows.length} user${selectedRows.length > 1 ? 's' : ''}...`);

        // ✅ Use setTimeout to allow React to process state updates before async operation
        setTimeout(async () => {
            let operationResult = null;
            let operationError = null;

            try {
                // Calculate end date if duration is specified
                const calculatedEndDate = licenseEndDate ||
                    (licenseDuration ? new Date(Date.now() + licenseDuration * 24 * 60 * 60 * 1000) : null);

                // Calculate duration in days (for backend)
                const durationDays = licenseEndDate ?
                    Math.ceil((licenseEndDate.getTime() - new Date().getTime()) / (24 * 60 * 60 * 1000)) :
                    licenseDuration;

                operationResult = await addLicensesToUsers("tobedefined", {
                    users: selectedRows,
                    tools: selectedTools,
                    numLicenses: numLics,
                    ...(selectedServer && { server: selectedServer }),
                    licServer: selectedLicServer || "ls01.trg.vlsiexpert.in:27030",
                    endDate: calculatedEndDate?.toISOString(),
                    duration: durationDays,  // Duration in days
                    expiryType: "calendar",  // Default expiry type
                    ctrlStatus: "PMAinit_UniqUserPage",  // UniqueUserTable specific status
                    lastUpdateRemark: `LicAddOprn: ${selectedTools.join(',')} (${numLics})`,  // Operation remark
                    source: "Gui-UniqueUserTable"  // Source identifier (25 chars max)
                });

                console.log('AddLicense result:', operationResult);

            } catch (error) {
                console.error("Error adding licenses:", error);
                operationError = error;
            }

            // ✅ ALWAYS SHOW TOAST (moved outside try-catch)
            if (operationResult?.data && operationResult.data.length > 0) {
                const successCount = operationResult.data.filter((item: any) => item.status === "success").length;
                const errorCount = operationResult.data.filter((item: any) => item.status === "error").length;
                const totalProcessed = operationResult.data.length;

                if (successCount > 0 && errorCount === 0) {
                    // All successful
                    eventBus.emit('show-toast', {
                        summary: '✅ All Licenses Added Successfully',
                        message: `Successfully added ${successCount} license${successCount > 1 ? 's' : ''} for ${selectedRows.length} user${selectedRows.length > 1 ? 's' : ''}`,
                        severity: 'success',
                    });
                    // ✅ CLEAR SELECTION AFTER SUCCESSFUL OPERATION
                    setSelectedRows([]);
                    // Close modal and refresh data
                    setAddLicenseModalVisible(false);
                    const counts = await getLicenseCountsForUsers();
                    setLicenseCounts(counts);
                    showResultAndHide('✅ Successfully added licenses!', true);
                } else if (successCount > 0 && errorCount > 0) {
                    // Partial success
                    eventBus.emit('show-toast', {
                        summary: '⚠️ Partial Success',
                        message: `Added ${successCount} license${successCount > 1 ? 's' : ''}, ${errorCount} failed. Check details for errors.`,
                        severity: 'warn',
                    });
                    // Don't clear selection on partial success - let user retry failed ones
                    showResultAndHide('⚠️ Some licenses added successfully!', true);
                } else if (errorCount > 0) {
                    // All failed
                    eventBus.emit('show-toast', {
                        summary: '❌ License Addition Failed',
                        message: `Failed to add ${errorCount} license${errorCount > 1 ? 's' : ''}. Please check configuration and try again.`,
                        severity: 'error',
                    });
                    showResultAndHide('❌ Failed to add licenses. Please try again.', false);
                }
            } else {
                // API call failed or returned no data
                eventBus.emit('show-toast', {
                    summary: '❌ Operation Failed',
                    message: (operationError as any)?.message || 'Failed to process license addition request. Please try again.',
                    severity: 'error',
                });
                showResultAndHide('❌ Failed to add licenses. Please try again.', false);
            }
        }, 0); // Next tick allows React to process state updates
    };



    useEffect(() => {
        const loadServers = async () => {
            try {
                console.log("Fetching available servers for UniqueUserTable");
                setServersLoading(true);

                const [infraServersData, licServersData] = await Promise.all([
                    getInfraServers(),
                    getLicServers({})
                ]);

                console.log("Infra Servers Data:", infraServersData);
                console.log("License Servers Data:", licServersData);

                // Transform server data for dropdowns
                if (infraServersData) {
                    const serverOptions = infraServersData.map((server: any) => ({
                        label: server.serverInfo || `${server.hostName}:${server.port}`,
                        value: server.serverInfo || `${server.hostName}:${server.port}`
                    }));
                    setServers(serverOptions);
                }

                if (licServersData) {
                    const licenseServerOptions = licServersData.map((server: any) => ({
                        label: `${server.serverFqdn}:${server.servicePort}`,
                        value: `${server.serverFqdn}:${server.servicePort}`
                    }));
                    setLicenseServers(licenseServerOptions);
                }

            } catch (error) {
                console.error("Error fetching servers for UniqueUserTable:", error);
                eventBus.emit('show-toast', {
                    summary: 'Error loading servers',
                    message: 'Failed to load server options. Using defaults.',
                    severity: 'warn',
                });
            } finally {
                setServersLoading(false);
            }
        };

        loadServers();
    }, []);


  const newHeaderStyle = useNew ? { backgroundColor: '#F8F9FB', color: '#1B2430' } : undefined;

  return (
    <div style={{ padding: useNew ? undefined : '20px' }}
      className={useNew ? 'font-plex' : undefined}
    >


        {/* UNIQUE USERS TOP BAR — token chrome when useNew (mirrors Server Accounts toolbar) */}
        <div className={useNew
          ? "mb-4 p-4 bg-surface-raised border-2 border-border-strong rounded"
          : "mb-4 p-4 bg-gradient-to-r from-slate-50 to-gray-50 rounded-lg border border-slate-200"
        }>
            <div className="flex justify-between items-center">
                {/* Compact header with inline stats */}
                <div className="flex items-center space-x-4">
                    {!useNew && <h2 className="text-lg font-bold text-slate-800">👥 Unique Users</h2>}
                    {useNew && <h2 className="text-sm font-semibold text-ink-primary">List</h2>}
                    <div className="flex space-x-2 text-xs">
                        <span className={useNew
                          ? "px-2 py-1 bg-accent/10 text-accent border border-accent/30 rounded-md font-medium"
                          : "px-2 py-1 bg-blue-100 text-blue-800 rounded-md font-medium"
                        }>
                            {filteredUsers.length} Users
                        </span>
                        <span className={useNew
                          ? "px-2 py-1 bg-success/10 text-success border border-success/30 rounded-md font-medium"
                          : "px-2 py-1 bg-green-100 text-green-800 rounded-md font-medium"
                        }>
                            {filteredUsers.reduce((sum, user) => sum + user.licenseCount, 0)} Licenses
                        </span>
                    </div>
                </div>

                {/* Professional action buttons */}
                <div className="flex flex-wrap gap-2 justify-end">
                    {canManagePrograms && (
                        <>
                            <Button
                                label="Dry-run classify"
                                icon="pi pi-eye"
                                className={useNew
                                  ? "px-4 py-2 bg-surface text-accent border border-accent font-medium rounded-lg hover:bg-accent/5 transition-all duration-200"
                                  : "px-4 py-2 bg-white text-indigo-700 border border-indigo-300 font-medium rounded-lg hover:bg-indigo-50 transition-all duration-200"
                                }
                                onClick={() => openClassifyReview('previewOnly')}
                                title="Preview proposed labels (program / instance tag). No save until you Apply."
                            />
                            <Button
                                label="Classify programs"
                                icon="pi pi-tags"
                                className={useNew
                                  ? "px-4 py-2 bg-accent text-white font-medium rounded-lg shadow-sm hover:bg-accent-hover transition-all duration-200 border-0"
                                  : "px-4 py-2 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-200 border-0"
                                }
                                onClick={() => openClassifyReview('applyFlow')}
                                title={selectedRows.length > 0
                                    ? `Review & apply labels for ${selectedRows.length} selected user(s)`
                                    : 'Review & apply labels (with limit). Does not attach to groups.'}
                            />
                            {workspaceEnabled && (
                                <Button
                                    label="Attach to group…"
                                    icon="pi pi-users"
                                    className={useNew
                                      ? "px-4 py-2 bg-surface text-accent border border-accent font-medium rounded-lg hover:bg-accent/5 transition-all duration-200"
                                      : "px-4 py-2 bg-white text-teal-700 border border-teal-300 font-medium rounded-lg hover:bg-teal-50 transition-all duration-200"
                                    }
                                    onClick={() => setAttachModalOpen(true)}
                                    disabled={selectedRows.length === 0}
                                    title={selectedRows.length > 0
                                        ? `Attach ${selectedRows.length} existing user(s) to a Batch/Group Instance`
                                        : 'Select existing Unique Users first'}
                                />
                            )}
                        </>
                    )}
                    <Button
                        label="Update Users"
                        icon="pi pi-refresh"
                        className={useNew
                          ? "px-4 py-2 bg-accent text-white font-medium rounded-lg shadow-sm hover:bg-accent-hover transition-all duration-200 border-0"
                          : "px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-200 border-0"
                        }
                        onClick={handleUpdateUsers}
                        loading={updatingUsers}
                        disabled={updatingUsers}
                    />
                    <Button
                        label="Configure Columns"
                        icon="pi pi-cog"
                        className={useNew
                          ? "px-4 py-2 bg-surface text-accent border border-accent font-medium rounded-lg hover:bg-accent/5 transition-all duration-200"
                          : "px-4 py-2 bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-200 border-0"
                        }
                        onClick={() => setColumnConfigOpen(true)}
                    />
                </div>
            </div>

            {/* Compact filters row — license / account filters (unchanged) */}
            <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3">
                {/* Global Search */}
                <div>
                    <label className={useNew ? "block text-xs font-medium text-ink-secondary mb-1" : "block text-xs font-medium text-gray-700 mb-1"}>{useNew ? 'Search' : '🔍 Search'}</label>
                    <InputText
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        placeholder="Search users..."
                        className="w-full"
                    />
                </div>

                {/* License Count Filter */}
                <div>
                    <label className={useNew ? "block text-xs font-medium text-ink-secondary mb-1" : "block text-xs font-medium text-gray-700 mb-1"}>{useNew ? 'License Count' : '📊 License Count'}</label>
                    <Dropdown
                        value={licenseFilter}
                        options={[
                            { label: 'All', value: null },
                            { label: 'No Licenses', value: 0 },
                            { label: '1-5 Licenses', value: '1-5' },
                            { label: '6+ Licenses', value: '6+' }
                        ]}
                        onChange={(e) => setLicenseFilter(e.value)}
                        placeholder="Filter by licenses"
                        className="w-full"
                    />
                </div>

                {/* Status Filter */}
                <div>
                    <label className={useNew ? "block text-xs font-medium text-ink-secondary mb-1" : "block text-xs font-medium text-gray-700 mb-1"}>{useNew ? 'Account Status' : '📈 Account Status'}</label>
                    <Dropdown
                        value={statusFilter}
                        options={[
                            { label: 'All', value: null },
                            { label: 'Active', value: 'active' },
                            { label: 'Inactive', value: 'inactive' }
                        ]}
                        onChange={(e) => setStatusFilter(e.value)}
                        placeholder="Filter by status"
                        className="w-full"
                    />
                </div>

                {/* Clear Filters */}
                <div className="flex items-end">
                    <Button
                        label="Clear Filters"
                        icon="pi pi-times"
                        className={useNew
                          ? "w-full px-2 py-2 bg-surface text-ink-secondary border border-border-strong font-medium rounded-lg hover:bg-surface-raised transition-all duration-200"
                          : "w-full px-2 py-2 bg-gray-500 text-white font-medium rounded-lg shadow-md hover:bg-gray-600 transition-all duration-200 border-0"
                        }
                        onClick={clearTopBarFilters}
                    />
                </div>
            </div>

            {/* Program classification filters */}
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                    <label className={useNew ? "block text-xs font-medium text-ink-secondary mb-1" : "block text-xs font-medium text-gray-700 mb-1"}>{useNew ? 'Program' : '🏷️ Program'}</label>
                    <Dropdown
                        value={programCodeFilter}
                        options={programFilterOptions.programCodes}
                        onChange={(e) => setProgramCodeFilter(e.value)}
                        placeholder="Filter by program"
                        className="w-full"
                    />
                </div>
                <div>
                    <label className={useNew ? "block text-xs font-medium text-ink-secondary mb-1" : "block text-xs font-medium text-gray-700 mb-1"}>{useNew ? 'Kind' : '📂 Kind'}</label>
                    <Dropdown
                        value={programKindFilter}
                        options={programFilterOptions.programKinds}
                        onChange={(e) => setProgramKindFilter(e.value)}
                        placeholder="Filter by kind"
                        className="w-full"
                    />
                </div>
                <div>
                    <label className={useNew ? "block text-xs font-medium text-ink-secondary mb-1" : "block text-xs font-medium text-gray-700 mb-1"}>{useNew ? 'Residual' : '🔗 Residual'}</label>
                    <Dropdown
                        value={membershipResidualFilter}
                        options={programFilterOptions.membershipResiduals}
                        onChange={(e) => setMembershipResidualFilter(e.value)}
                        placeholder="Filter by residual"
                        className="w-full"
                    />
                </div>
            </div>

            {/* Program summary strip */}
            <div className={useNew
              ? "mt-3 p-3 bg-surface border border-border-strong rounded flex flex-wrap items-center gap-2 text-xs"
              : "mt-3 p-3 bg-slate-100 border border-slate-200 rounded-lg flex flex-wrap items-center gap-2 text-xs"
            }>
                <span className={useNew ? "font-semibold text-ink-primary" : "font-semibold text-slate-700"}>
                    {useNew ? 'Programs:' : '📊 Programs:'}
                </span>
                {Object.entries(programSummary.counts)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([code, count]) => (
                        <span
                            key={code}
                            className={useNew
                              ? "px-2 py-1 bg-accent/10 text-accent border border-accent/30 rounded-md font-medium"
                              : "px-2 py-1 bg-blue-100 text-blue-800 rounded-md font-medium"
                            }
                        >
                            {code}: {count}
                        </span>
                    ))}
                <span className={useNew
                  ? "px-2 py-1 bg-warning/10 text-warning border border-warning/30 rounded-md font-medium"
                  : "px-2 py-1 bg-amber-100 text-amber-800 rounded-md font-medium"
                }>
                    Orphan residual: {programSummary.orphanCount}
                </span>
            </div>
        </div>

    <DataTable
        value={filteredUsers}
        // paginator
        // rows={20}
        dataKey="sid"
        first={first}
        selectionMode="checkbox"
        rowClassName={(data, index) => useNew
          ? `row-${index} cursor-pointer hover:bg-surface-raised transition-colors`
          : `row-${index} cursor-pointer hover:bg-blue-50 transition-colors`}
        selection={selectedRows}

        onSelectionChange={(e) => setSelectedRows(e.value)}


        filters={{
            sid: { value: null, matchMode: 'contains' },
            userId: { value: null, matchMode: 'contains' },
            email: { value: null, matchMode: 'contains' },
            fullName: { value: null, matchMode: 'contains' },
            mobile: { value: null, matchMode: 'contains' },
            servers: { value: null, matchMode: 'contains' },
            numActiveAccounts: { value: null, matchMode: 'equals' },
            createdAt: { value: null, matchMode: 'dateIs' },
        }}
        filterDisplay="menu"
            paginator
            rows={rows}
            rowsPerPageOptions={[10, 20, 50, 100]}
            paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
            currentPageReportTemplate="{first} to {last} of {totalRecords}"
            onPage={(event) => {
                setFirst(event.first);
                setRows(event.rows);
            }}
            className={useNew ? "p-datatable-gridlines new-gui-datatable border-2 border-border-strong rounded overflow-hidden" : "p-datatable-gridlines"}
            onRowClick={(e) => {
                // Prevent navigation if clicking on checkbox or selection elements
                const target = e.originalEvent.target as HTMLElement;
                const isCheckboxClick = target.closest('.p-checkbox') ||
                                       target.closest('.p-checkbox-box') ||
                                       target.closest('[data-pc-name="checkbox"]') ||
                                       target.closest('.p-selection-column');

                if (!isCheckboxClick) {
                    navigate(`/user/${e.data.email}`);
                }
            }}
    >

        <Column
            selectionMode="multiple"
            headerStyle={useNew
              ? { width: '3em', textAlign: 'center', backgroundColor: '#F8F9FB' }
              : { width: '3em', textAlign: 'center', backgroundColor: '#e4e4e4' }}
            bodyStyle={useNew
              ? { textAlign: 'center', backgroundColor: '#FFFFFF', padding: '0.5rem' }
              : { textAlign: 'center', backgroundColor: '#d0e0d0', padding: '0.5rem' }}
        />
        <Column
            header=""
            body={<i className={useNew ? "pi pi-chevron-right text-accent text-lg" : "pi pi-chevron-right text-blue-500 text-lg"}></i>}
            style={{ width: '2.5em', textAlign: 'center', cursor: 'pointer' }}
            bodyStyle={useNew
              ? { textAlign: 'center', backgroundColor: '#F8F9FB', padding: '0.5rem' }
              : { textAlign: 'center', backgroundColor: '#f8f9fa', padding: '0.5rem' }}
        />
        {columns.filter(col => col.visible).map((column) => {
            if (column.key === 'fullName') {
                return (
                    <Column
                        key={column.key}
                        field={column.key}
                        header={column.label}
                        body={(rowData: UniqueUser) => rowData.fullName || "N/A"}
                        sortable={column.sortable}
                        filter
                        filterPlaceholder={`Search by ${column.label}`}
                        headerStyle={newHeaderStyle}
                    />
                );
            } else if (column.key === 'createdAt') {
                return (
                    <Column
                        key={column.key}
                        field={column.key}
                        header={column.label}
                        body={(rowData: UniqueUser) => new Date(rowData.createdAt).toLocaleString()}
                        sortable={column.sortable}
                        headerStyle={newHeaderStyle}
                    />
                );
            } else if (column.key === 'userId') {
                return (
                    <Column
                        key={column.key}
                        field={column.key}
                        header={column.label}
                        body={(rowData: UniqueUser) =>
                            (rowData.userIds && rowData.userIds.length > 0)
                                ? rowData.userIds.join(', ')
                                : (rowData.userId || '—')
                        }
                        sortable={column.sortable}
                        filter
                        filterPlaceholder={`Search by ${column.label}`}
                        headerStyle={newHeaderStyle}
                    />
                );
            } else if (column.key === 'programCode') {
                return (
                    <Column
                        key={column.key}
                        field={column.key}
                        header={column.label}
                        sortable={column.sortable}
                        headerStyle={newHeaderStyle}
                        body={(rowData: UniqueUser) => (
                            <div className="flex items-center gap-2">
                                <span>{rowData.programCode || '—'}</span>
                                {canManagePrograms && (
                                    <Button
                                        icon="pi pi-pencil"
                                        className="p-button-text p-button-sm p-0"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openProgramEditModal(rowData);
                                        }}
                                        tooltip="Edit program"
                                        tooltipOptions={{ position: 'top' }}
                                    />
                                )}
                            </div>
                        )}
                    />
                );
            } else if (column.key === 'programKind') {
                return (
                    <Column
                        key={column.key}
                        field={column.key}
                        header={column.label}
                        body={(rowData: UniqueUser) => rowData.programKind || '—'}
                        sortable={column.sortable}
                        headerStyle={newHeaderStyle}
                    />
                );
            } else if (column.key === 'programInstanceId') {
                return (
                    <Column
                        key={column.key}
                        field={column.key}
                        header={column.label}
                        body={(rowData: UniqueUser) => rowData.programInstanceId || '—'}
                        sortable={column.sortable}
                        headerStyle={newHeaderStyle}
                    />
                );
            } else if (column.key === 'primaryGroupInstance') {
                return (
                    <Column
                        key={column.key}
                        field={column.key}
                        header={column.label}
                        body={(rowData: UniqueUser) => rowData.primaryGroupInstance || '—'}
                        sortable={column.sortable}
                        filter
                        filterPlaceholder="Search group instance"
                        headerStyle={newHeaderStyle}
                    />
                );
            } else if (column.key === 'membershipResidual') {
                return (
                    <Column
                        key={column.key}
                        field={column.key}
                        header={column.label}
                        sortable={column.sortable}
                        headerStyle={newHeaderStyle}
                        body={(rowData: UniqueUser) => {
                            const label = formatMembershipResidual(rowData.membershipResidual);
                            if (label === 'Orphan') {
                                return (
                                    <span className={useNew
                                      ? "px-2 py-0.5 rounded-full text-xs font-medium bg-warning/10 text-warning border border-warning/30"
                                      : "px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                                    }>
                                        {label}
                                    </span>
                                );
                            }
                            return label;
                        }}
                    />
                );
            } else {
                return (
                    <Column
                        key={column.key}
                        field={column.key}
                        header={column.label}
                        sortable={column.sortable}
                        filter={column.key !== 'numActiveAccounts'}
                        filterPlaceholder={column.key !== 'numActiveAccounts' ? `Search by ${column.label}` : undefined}
                        headerStyle={newHeaderStyle}
                    />
                );
            }
        })}
        <Column
            field="licenseCount"
            header="License Count"
            sortable
            style={{ textAlign: 'center', fontWeight: 'bold' }}
            headerStyle={useNew ? { backgroundColor: '#F8F9FB', color: '#1B2430', textAlign: 'center' } : undefined}
            bodyStyle={useNew
              ? { textAlign: 'center', backgroundColor: 'rgba(26, 127, 75, 0.08)', color: '#1A7F4B', fontWeight: 600 }
              : { textAlign: 'center', backgroundColor: '#e8f5e8' }}
        />
    </DataTable>

    {/* License Operations Bar - BELOW TABLE */}
    {selectedRows.length > 0 && (
        <div className={useNew
          ? "mt-4 p-4 bg-accent/5 border border-accent/20 rounded-lg"
          : "mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200"
        }>
            <div className="flex justify-between items-center mb-3">
                <h3 className={useNew ? "text-base font-semibold text-ink-primary" : "text-lg font-semibold text-blue-800"}>
                    {useNew
                      ? `License Operations for ${selectedRows.length} Selected User${selectedRows.length > 1 ? 's' : ''}`
                      : `🔧 License Operations for ${selectedRows.length} Selected User${selectedRows.length > 1 ? 's' : ''}`}
                </h3>
                <div className="flex gap-2">
                    <Button
                        label="Add Licenses"
                        icon="pi pi-plus"
                        className={useNew
                          ? "px-4 py-2 bg-success text-white font-medium rounded-lg shadow-sm hover:opacity-90 transition-all duration-200 border-0"
                          : "px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white font-medium rounded-lg shadow-md hover:from-green-700 hover:to-green-800 hover:shadow-lg transition-all duration-200 border-0"
                        }
                        onClick={() => setAddLicenseModalVisible(true)}
                    />
                    <Button
                        label="View Licenses"
                        icon="pi pi-eye"
                        className={useNew
                          ? "px-4 py-2 bg-surface text-accent border border-accent font-medium rounded-lg hover:bg-accent/5 transition-all duration-200"
                          : "px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg shadow-md hover:from-blue-700 hover:to-blue-800 hover:shadow-lg transition-all duration-200 border-0"
                        }
                        onClick={async () => {
                            await loadCurrentLicenses();
                            setUpdateLicenseModalVisible(true);
                            // ✅ CLEAR SELECTION WHEN VIEWING LICENSES
                            setSelectedRows([]);
                        }}
                    />
                    <Button
                        label="Generate NFS Home"
                        icon="pi pi-folder-open"
                        className={useNew
                          ? "px-4 py-2 bg-accent text-white font-medium rounded-lg shadow-sm hover:opacity-90 transition-all duration-200 border-0"
                          : "px-4 py-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white font-medium rounded-lg shadow-md hover:from-indigo-700 hover:to-indigo-800 hover:shadow-lg transition-all duration-200 border-0"
                        }
                        loading={bulkNfsHomeRunning}
                        disabled={bulkNfsHomeRunning}
                        onClick={handleBulkGenerateNfsHome}
                    />
                </div>
            </div>
        </div>
    )}

    {/* IC-042: Bulk "Generate NFS Home" results — per-row outcome, not a blended toast */}
    <Dialog
        header={`Generate NFS Home — ${bulkNfsHomeResults.length} user${bulkNfsHomeResults.length === 1 ? '' : 's'}`}
        visible={bulkNfsHomeResultsVisible}
        style={{ width: '640px' }}
        onHide={() => setBulkNfsHomeResultsVisible(false)}
    >
        <div className="p-fluid">
            {bulkNfsHomeResults.map((r) => (
                <div
                    key={r.userId}
                    className="flex items-start justify-between gap-3 py-2 border-b border-gray-100 last:border-0"
                >
                    <div className="font-medium text-gray-800">{r.userId}</div>
                    <div className="flex items-center gap-2 text-right">
                        <span
                            className={
                                r.outcome === 'failed'
                                    ? 'text-red-600 text-sm font-semibold'
                                    : r.outcome === 'already-ready'
                                        ? 'text-gray-500 text-sm font-semibold'
                                        : 'text-green-700 text-sm font-semibold'
                            }
                        >
                            {r.outcome === 'failed' ? 'Failed' : r.outcome === 'already-ready' ? 'Already ready' : 'OK'}
                        </span>
                        <span className="text-xs text-gray-500">{r.detail}</span>
                    </div>
                </div>
            ))}
        </div>
    </Dialog>

    {/* Column Configuration Dialog */}
    <Dialog
        header="Configure Visible Columns"
        visible={columnConfigOpen}
        style={{ width: '450px' }}
        footer={
            <div>
                <Button
                    label="Reset to Default"
                    icon="pi pi-refresh"
                    className="p-button-secondary"
                    onClick={resetColumnsToDefault}
                />
                <Button
                    label="Close"
                    icon="pi pi-times"
                    onClick={() => setColumnConfigOpen(false)}
                />
            </div>
        }
        onHide={() => setColumnConfigOpen(false)}
    >
        <div className="p-fluid">
            <p style={{ marginBottom: '1rem', color: '#666' }}>
                Select which columns to display in the table:
            </p>
            <div className="grid">
                {columns.map((column) => (
                    <div key={column.key} className="col-6" style={{ marginBottom: '0.5rem' }}>
                        <Checkbox
                            inputId={column.key}
                            checked={column.visible}
                            onChange={() => handleColumnToggle(column.key)}
                        />
                        <label htmlFor={column.key} style={{ marginLeft: '0.5rem' }}>
                            {column.label}
                        </label>
                    </div>
                ))}
            </div>
        </div>
    </Dialog>

    {/* Add License Modal */}
    <Dialog
        header="🔑 Add New Licenses"
        visible={addLicenseModalVisible}
        style={{ width: '800px', maxWidth: '90vw' }}
        onHide={() => setAddLicenseModalVisible(false)}
        footer={
            <div className="flex justify-end gap-2">
                <Button
                    label="Cancel"
                    icon="pi pi-times"
                    className="p-button-secondary"
                    onClick={() => setAddLicenseModalVisible(false)}
                />
                <Button
                    label="Add Licenses"
                    icon="pi pi-plus"
                    className="p-button-success"
                    loading={addLicenseLoading}
                    disabled={addLicenseLoading}
                    onClick={handleAddLicenses}
                />
            </div>
        }
    >
        <div className="space-y-6 p-2">
            {/* Selection Summary */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-lg font-semibold text-blue-800 mb-3">👥 Selected Users</h4>
                <p className="text-sm text-gray-600 mb-2">
                    Adding licenses for <strong>{selectedRows.length}</strong> user{selectedRows.length > 1 ? 's' : ''}
                </p>
                <div className="max-h-32 overflow-y-auto border border-gray-200 rounded p-2 bg-white">
                    <ul className="list-disc list-inside text-sm">
                        {selectedRows.map((user, index) => (
                            <li key={index} className="mb-1">
                                {(user.userIds && user.userIds.length > 0 ? user.userIds.join(', ') : user.userId)} ({user.email})
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* License Configuration */}
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="text-lg font-semibold text-green-800 mb-3">📋 License Configuration</h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tools</label>
                        <MultiSelect
                            value={selectedTools}
                            options={tools}
                            onChange={(e) => setSelectedTools(e.value)}
                            placeholder="Select tools"
                            className="w-full"
                            disabled={toolsLoading}
                            showClear
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">License Count</label>
                        <InputNumber
                            value={numLics}
                            onValueChange={(e) => e.value && setNumLics(e.value)}
                            min={1}
                            max={10}
                            showButtons
                            className="w-full"
                        />
                    </div>
                </div>
            </div>

            {/* Server Configuration */}
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <h4 className="text-lg font-semibold text-purple-800 mb-3">🖥️ Server Configuration</h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Target Server (Optional)</label>
                        <Dropdown
                            value={selectedServer}
                            options={servers}
                            onChange={(e) => setSelectedServer(e.value)}
                            placeholder="Select target server"
                            className="w-full"
                            disabled={serversLoading}
                            showClear
                        />
                        <p className="text-xs text-gray-500 mt-1">Leave empty for default server</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">License Server</label>
                        <Dropdown
                            value={selectedLicServer}
                            options={licenseServers}
                            onChange={(e) => setSelectedLicServer(e.value)}
                            placeholder="Select license server"
                            className="w-full"
                            disabled={serversLoading}
                            showClear
                        />
                    </div>
                </div>
            </div>

            {/* Date Configuration */}
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h4 className="text-lg font-semibold text-yellow-800 mb-3">📅 License Duration</h4>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                        <Calendar
                            value={licenseEndDate}
                            onChange={(e) => e.value && setLicenseEndDate(e.value)}
                            placeholder="Select end date"
                            className="w-full"
                            showIcon
                            minDate={new Date()}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">OR Duration (days)</label>
                        <InputNumber
                            value={licenseDuration}
                            onValueChange={(e) => e.value && setLicenseDuration(e.value)}
                            min={1}
                            max={365}
                            showButtons
                            className="w-full"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            {licenseDuration} days from today = {new Date(Date.now() + licenseDuration * 24 * 60 * 60 * 1000).toLocaleDateString()}
                        </p>
                    </div>
                </div>
            </div>

            {/* Summary Section */}
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-800 mb-3">📝 Configuration Summary</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Users:</span>
                        <span className="text-gray-900">{selectedRows.length}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Tools:</span>
                        <span className="text-gray-900">{selectedTools.length}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Licenses per tool:</span>
                        <span className="text-gray-900">{numLics}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Total licenses:</span>
                        <span className="text-gray-900 font-semibold">{selectedRows.length * selectedTools.length * numLics}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Target Server:</span>
                        <span className="text-gray-900">{selectedServer || 'Default'}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="font-medium text-gray-600">License Server:</span>
                        <span className="text-gray-900">{selectedLicServer || 'Default'}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="font-medium text-gray-600">End Date:</span>
                        <span className="text-gray-900">
                            {licenseEndDate ? licenseEndDate.toLocaleDateString() :
                             (licenseDuration ? new Date(Date.now() + licenseDuration * 24 * 60 * 60 * 1000).toLocaleDateString() : 'Not specified')}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </Dialog>

    {/* View License Modal */}
    <Dialog
        header="📋 View Existing Licenses"
        visible={updateLicenseModalVisible}
        style={{ width: '1000px', maxWidth: '95vw' }}
        onHide={() => setUpdateLicenseModalVisible(false)}
        footer={
            <div className="flex justify-end">
                <Button
                    label="Close"
                    icon="pi pi-times"
                    className="p-button-secondary"
                    onClick={() => setUpdateLicenseModalVisible(false)}
                />
            </div>
        }
    >
        <div className="space-y-4">
            {/* Enhanced Filters */}
            <div className="p-3 bg-white border border-gray-200 rounded-lg">
                {/* Filter Header with Clear Button */}
                <div className="flex justify-between items-center mb-3">
                    <h4 className="text-sm font-semibold text-gray-800">🔍 Advanced Filters</h4>
                    <Button
                        label="Clear All"
                        icon="pi pi-times"
                        size="small"
                        className="p-button-secondary p-button-outlined"
                        onClick={clearAllViewFilters}
                        disabled={!hasActiveViewFilters}
                    />
                </div>

                {/* Filter Controls */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Tool Filter */}
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Tools:</label>
                        <MultiSelect
                            value={selectedViewTools}
                            options={tools}
                            onChange={(e) => setSelectedViewTools(e.value)}
                            placeholder="All tools"
                            showClear
                            className="w-full"
                            disabled={toolsLoading}
                        />
                    </div>

                    {/* Status Filter */}
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Status:</label>
                        <MultiSelect
                            value={selectedStatuses}
                            options={[
                                { label: 'Active', value: 'Active' },
                                { label: 'Disabled', value: 'Disabled' },
                                { label: 'Expired', value: 'Expired' }
                            ]}
                            onChange={(e) => setSelectedStatuses(e.value)}
                            placeholder="All statuses"
                            showClear
                            className="w-full"
                        />
                    </div>

                    {/* End Date Filter */}
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">End Date:</label>
                        <div className="flex gap-1">
                            <Dropdown
                                value={endDateOperator}
                                options={[
                                    { label: '≥', value: 'gte' },
                                    { label: '≤', value: 'lte' }
                                ]}
                                onChange={(e) => setEndDateOperator(e.value)}
                                className="w-12"
                            />
                            <Calendar
                                value={endDateFilter}
                                onChange={(e) => e.value && setEndDateFilter(e.value)}
                                placeholder="Select date"
                                showIcon
                                className="flex-1"
                            />
                        </div>
                    </div>
                </div>

                {/* Active Filters Summary */}
                {hasActiveViewFilters && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex flex-wrap gap-1">
                            {selectedViewTools.map(tool => (
                                <span key={tool} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                    Tool: {tool} ×
                                </span>
                            ))}
                            {selectedStatuses.map(status => (
                                <span key={status} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                    Status: {status} ×
                                </span>
                            ))}
                            {endDateFilter && (
                                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                                    End Date {endDateOperator === 'gte' ? '≥' : '≤'} {endDateFilter.toLocaleDateString()} ×
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-base font-semibold text-blue-800 mb-2">📋 Current Licenses for Selected Users</h4>
                <p className="text-sm text-gray-600 mb-2">
                    Viewing {currentLicenses.filter(license =>
                        selectedViewTools.length === 0 || selectedViewTools.includes(license.toolName)
                    ).length} existing license{currentLicenses.length > 1 ? 's' : ''}
                    {selectedViewTools.length > 0 && ` for tools: ${selectedViewTools.join(', ')}`}
                </p>

                <DataTable
                    value={currentLicenses.filter(license => {
                        // Tool filter
                        const toolMatch = selectedViewTools.length === 0 ||
                                         selectedViewTools.includes(license.toolName);

                        // Status filter
                        const statusMatch = selectedStatuses.length === 0 ||
                                           selectedStatuses.includes(license.status);

                        // End date filter
                        const endDateMatch = !endDateFilter ||
                                           (license.plannedEndDate &&
                                            (endDateOperator === 'gte' ?
                                             new Date(license.plannedEndDate) >= endDateFilter :
                                             new Date(license.plannedEndDate) <= endDateFilter));

                        return toolMatch && statusMatch && endDateMatch;
                    })}
                    className="p-datatable-sm"
                    showGridlines
                >
                    <Column
                        field="userId"
                        header="User ID"
                        style={{ width: '120px' }}
                        sortable
                    />
                    <Column
                        field="toolName"
                        header="Tool"
                        style={{ width: '150px' }}
                        sortable
                    />
                    <Column
                        field="licenseCount"
                        header="Count"
                        style={{ width: '100px', textAlign: 'center' }}
                        sortable
                    />
                    <Column
                        field="startDate"
                        header="Start Date"
                        style={{ width: '140px' }}
                        sortable
                        body={(row) => row.startDate ? new Date(row.startDate).toLocaleDateString() : 'N/A'}
                    />
                    <Column
                        field="plannedEndDate"
                        header="Planned End Date"
                        style={{ width: '160px' }}
                        sortable
                        body={(row) => row.plannedEndDate ? new Date(row.plannedEndDate).toLocaleDateString() : 'N/A'}
                    />
                    <Column
                        field="status"
                        header="Status"
                        style={{ width: '120px', textAlign: 'center' }}
                        sortable
                        body={(row) => (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                row.status === 'Active' ? 'bg-green-100 text-green-800' :
                                row.status === 'Disabled' ? 'bg-red-100 text-red-800' :
                                row.status === 'Expired' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                            }`}>
                                {row.status || 'Unknown'}
                            </span>
                        )}
                    />
                </DataTable>
            </div>

            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="text-base font-semibold text-gray-800 mb-2">📊 Summary</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Total Licenses:</span>
                        <span className="text-gray-900">
                            {currentLicenses.filter(license => {
                                const toolMatch = selectedViewTools.length === 0 ||
                                                 selectedViewTools.includes(license.toolName);
                                const statusMatch = selectedStatuses.length === 0 ||
                                                   selectedStatuses.includes(license.status);
                                const endDateMatch = !endDateFilter ||
                                                   (license.plannedEndDate &&
                                                    (endDateOperator === 'gte' ?
                                                     new Date(license.plannedEndDate) >= endDateFilter :
                                                     new Date(license.plannedEndDate) <= endDateFilter));
                                return toolMatch && statusMatch && endDateMatch;
                            }).length}
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Active:</span>
                        <span className="text-green-600 font-semibold">
                            {currentLicenses.filter(license => {
                                const toolMatch = selectedViewTools.length === 0 ||
                                                 selectedViewTools.includes(license.toolName);
                                const statusMatch = selectedStatuses.length === 0 ||
                                                   selectedStatuses.includes(license.status);
                                const endDateMatch = !endDateFilter ||
                                                   (license.plannedEndDate &&
                                                    (endDateOperator === 'gte' ?
                                                     new Date(license.plannedEndDate) >= endDateFilter :
                                                     new Date(license.plannedEndDate) <= endDateFilter));
                                return toolMatch && statusMatch && endDateMatch && license.status === 'Active';
                            }).length}
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Disabled:</span>
                        <span className="text-red-600 font-semibold">
                            {currentLicenses.filter(license => {
                                const toolMatch = selectedViewTools.length === 0 ||
                                                 selectedViewTools.includes(license.toolName);
                                const statusMatch = selectedStatuses.length === 0 ||
                                                   selectedStatuses.includes(license.status);
                                const endDateMatch = !endDateFilter ||
                                                   (license.plannedEndDate &&
                                                    (endDateOperator === 'gte' ?
                                                     new Date(license.plannedEndDate) >= endDateFilter :
                                                     new Date(license.plannedEndDate) <= endDateFilter));
                                return toolMatch && statusMatch && endDateMatch && license.status === 'Disabled';
                            }).length}
                        </span>
                    </div>
                    <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Expired:</span>
                        <span className="text-yellow-600 font-semibold">
                            {currentLicenses.filter(license => {
                                const toolMatch = selectedViewTools.length === 0 ||
                                                 selectedViewTools.includes(license.toolName);
                                const statusMatch = selectedStatuses.length === 0 ||
                                                   selectedStatuses.includes(license.status);
                                const endDateMatch = !endDateFilter ||
                                                   (license.plannedEndDate &&
                                                    (endDateOperator === 'gte' ?
                                                     new Date(license.plannedEndDate) >= endDateFilter :
                                                     new Date(license.plannedEndDate) <= endDateFilter));
                                return toolMatch && statusMatch && endDateMatch && license.status === 'Expired';
                            }).length}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </Dialog>

    {/* Edit Program Modal */}
    <Dialog
        header="Edit program"
        visible={programEditModalVisible}
        style={{ width: '480px', maxWidth: '95vw' }}
        onHide={() => {
            if (!programEditSaving) {
                setProgramEditModalVisible(false);
                setProgramEditUser(null);
            }
        }}
        footer={
            <div className="flex justify-end gap-2">
                <Button
                    label="Cancel"
                    icon="pi pi-times"
                    className="p-button-secondary"
                    disabled={programEditSaving}
                    onClick={() => {
                        setProgramEditModalVisible(false);
                        setProgramEditUser(null);
                    }}
                />
                <Button
                    label="Save"
                    icon="pi pi-check"
                    loading={programEditSaving}
                    disabled={programEditSaving}
                    onClick={handleSaveProgramOverride}
                />
            </div>
        }
    >
        {programEditUser && (
            <div className="space-y-4 p-2">
                <p className="text-sm text-gray-600">
                    Manual override for <strong>{programEditUser.email}</strong>
                    {programEditUser.programSource && (
                        <span className="block text-xs mt-1">Current source: {programEditUser.programSource}</span>
                    )}
                </p>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Program code *</label>
                    <InputText
                        value={editProgramCode}
                        onChange={(e) => setEditProgramCode(e.target.value)}
                        className="w-full"
                        placeholder="e.g. DV-2026-Q1"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Kind</label>
                    <InputText
                        value={editProgramKind}
                        onChange={(e) => setEditProgramKind(e.target.value)}
                        className="w-full"
                        placeholder="Optional"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Instance ID</label>
                    <InputText
                        value={editProgramInstanceId}
                        onChange={(e) => setEditProgramInstanceId(e.target.value)}
                        className="w-full"
                        placeholder="Optional"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Membership residual</label>
                    <Dropdown
                        value={editMembershipResidual}
                        options={[
                            { label: 'Not set', value: null },
                            { label: 'Orphan', value: 'orphan' },
                            { label: 'None', value: 'none' },
                        ]}
                        onChange={(e) => setEditMembershipResidual(e.value)}
                        placeholder="Optional"
                        className="w-full"
                        showClear
                    />
                </div>
            </div>
        )}
    </Dialog>



    {workspaceEnabled && (
      <AttachExistingUsersModal
        open={attachModalOpen || attachFromClassifyUsers != null}
        onClose={() => {
          setAttachModalOpen(false);
          setAttachFromClassifyUsers(null);
        }}
        users={
          attachFromClassifyUsers != null
            ? attachFromClassifyUsers
            : selectedRows.map((u) => ({
                id: u.id,
                userId: u.userId,
                email: u.email,
                programCode: u.programCode,
              }))
        }
        onAttached={async () => {
          setSelectedRows([]);
          setAttachFromClassifyUsers(null);
          if (onUsersUpdated) await onUsersUpdated();
          await refreshUserData();
        }}
      />
    )}

    {canManagePrograms && (
      <ClassifyProgramsReviewModal
        open={classifyReviewOpen}
        onClose={() => setClassifyReviewOpen(false)}
        selectedUsers={selectedRows}
        intent={classifyIntent}
        onApplied={async () => {
          await refreshUserData();
          setSelectedRows([]);
        }}
        onRequestAttach={
          workspaceEnabled
            ? (users) => {
                setAttachFromClassifyUsers(users);
                setAttachModalOpen(true);
              }
            : undefined
        }
      />
    )}

</div>
  );
  };

  export default UniqueUserTable
